/**
 * Client-side abuse guards on the pre-registration intake (WWW-S024).
 *
 * The form POSTs JSON straight from the browser to a public, anonymous
 * Salesforce Sites REST endpoint. That request never passes the www CloudFront
 * edge, so no WAF rule can see it, and there is no backend in this repo to put
 * a rate limit in. Whether the POST gets fronted at all is Aaron's pending
 * architectural decision — see `docs/lead-intake-protection.md`. These two
 * guards are the part that is decision-INDEPENDENT and ships now:
 *
 * 1. A honeypot: a decoy text field, off-screen but present in the DOM, that a
 *    form-filling script populates and a visitor never sees.
 * 2. A minimum time between the first interaction with the form and the POST.
 *    A human needs minutes for a seven-step wizard; a script needs milliseconds.
 *
 * Both drop the submission SILENTLY — the caller shows the ordinary success
 * state — so a bot author gets no signal to tune against. They only stop bots
 * that drive the page; a `curl` straight at the Salesforce endpoint bypasses
 * both, which is exactly what the server-side companion story (SF-S092) and/or
 * the proxy decision cover.
 *
 * Both are cheap and both risk a false positive, so each is written to fail
 * OPEN wherever the signal is ambiguous (whitespace-only decoy, unparseable
 * threshold) — never-lose-a-lead outranks catching one more bot.
 */

/**
 * Name of the decoy field. This exact string is a CONTRACT with Salesforce: the
 * Apex intake will reject any payload that carries this key once SF-S092 ships,
 * which is the backstop for a bot that skips this client. That story is still
 * in the backlog, so no server-side rejection exists yet. Renaming this here
 * means renaming it there in the same breath.
 */
export const HONEYPOT_FIELD = "website_url";

/**
 * Seconds that must pass between the first interaction with the form and the
 * POST. Three is far below what a real seven-step registration takes (minutes)
 * and far above what a script needs, so it buys the whole margin without
 * risking a visitor who restored saved answers and moved fast.
 *
 * This is the single source of truth for the threshold. The markup advertises
 * it as `data-min-submit-seconds` so the Playwright smoke can wait it out
 * rather than hard-code a rival number; `minSecondsFor` reads that attribute
 * and falls back here.
 */
export const MIN_SECONDS_BEFORE_SUBMIT = 3;

export interface GuardOptions {
  /** Injectable clock — the unit tests move it by hand to test the boundary. */
  now?: () => number;
}

export interface SubmissionGuard {
  /** Epoch ms of the first interaction, or undefined if nobody has touched it. */
  firstInteractionAt: () => number | undefined;
  /** True when the decoy field carries a real value. */
  honeypotFilled: () => boolean;
  /** True when the POST is arriving sooner than a human could have got here. */
  tooFast: () => boolean;
  /** True when the submission must be dropped (and faked as a success). */
  shouldDrop: () => boolean;
  /** The threshold actually in force, in seconds. */
  minSeconds: number;
}

/**
 * The threshold this form is running, in seconds.
 *
 * Reads `data-min-submit-seconds` off the form and falls back to
 * `MIN_SECONDS_BEFORE_SUBMIT`. Anything unparseable or negative falls back too:
 * a typo in the markup must not silently switch the guard off.
 */
export function minSecondsFor(form: HTMLFormElement): number {
  const raw = Number(form.dataset.minSubmitSeconds);
  if (!Number.isFinite(raw) || raw < 0) return MIN_SECONDS_BEFORE_SUBMIT;
  return raw;
}

/**
 * Start watching the form and return the guard the submit handler consults.
 *
 * FIRST-INTERACTION SIGNAL: the earliest of `pointerdown`, `keydown`,
 * `focusin`, `input` and `change`, all listened for on the form itself (each
 * bubbles; `focus` does not, which is why it isn't among them). Every one of
 * the five is load-bearing, because no single event covers every real visitor:
 *
 * - `pointerdown` — mouse, touch and pen. This one is NOT optional cover for
 *   `focusin`: **Safari (macOS and iOS) and Firefox on macOS do not focus a
 *   `<button>` when it is clicked**, so a visitor whose answers were restored
 *   from sessionStorage — who fills nothing and only clicks Next through the
 *   steps — would otherwise reach Submit having fired no event at all, and be
 *   silently dropped however slowly they went. That is a lost real lead, not an
 *   edge case, and it is why this listener exists.
 * - `keydown` — the keyboard-only visitor who tabs to a button and presses
 *   Enter or Space. Tabbing already fires `focusin`; this is belt-and-braces
 *   and costs nothing.
 * - `focusin` — the visitor who clicks into a field and reads before typing.
 * - `input` / `change` — a password manager that autofills without ever
 *   focusing anything.
 *
 * Taking the earliest of the set starts the clock as early as possible, which
 * is the conservative direction: it can only ever make the guard LESS likely to
 * drop a real lead. A submit with none of the five recorded is still dropped —
 * that is a headless script calling `submit()` on a form nobody touched.
 */
export function initSubmissionGuard(
  form: HTMLFormElement,
  opts: GuardOptions = {},
): SubmissionGuard {
  const now = opts.now ?? (() => Date.now());
  const minSeconds = minSecondsFor(form);
  let firstInteraction: number | undefined;

  const mark = (): void => {
    // First one wins: this measures time spent in the form, not the gap since
    // the last keystroke.
    firstInteraction ??= now();
  };
  // Order is irrelevant — whichever fires first wins — but the breadth is not;
  // see the FIRST-INTERACTION SIGNAL note above before removing any of them.
  for (const type of ["pointerdown", "keydown", "focusin", "input", "change"]) {
    form.addEventListener(type, mark);
  }

  const honeypotFilled = (): boolean => {
    const decoy = form.querySelector<HTMLInputElement>(
      `[name="${HONEYPOT_FIELD}"]`,
    );
    // Whitespace is not a fill — an errant space must not cost a real lead.
    return (decoy?.value ?? "").trim() !== "";
  };

  const tooFast = (): boolean => {
    // No interaction recorded at all means the DOM was driven, not used.
    if (firstInteraction === undefined) return true;
    return now() - firstInteraction < minSeconds * 1000;
  };

  return {
    firstInteractionAt: () => firstInteraction,
    honeypotFilled,
    tooFast,
    shouldDrop: () => honeypotFilled() || tooFast(),
    minSeconds,
  };
}
